/**
 * Date e orari: i timestamp arrivano dall'API in ISO UTC e si mostrano sempre in Europe/Rome.
 * Nessuna libreria: basta Intl.
 */
export const FUSO = 'Europe/Rome';
const LOCALE = 'it-IT';

const fmtData = new Intl.DateTimeFormat(LOCALE, { timeZone: FUSO, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtDataBreve = new Intl.DateTimeFormat(LOCALE, { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtOra = new Intl.DateTimeFormat(LOCALE, { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });
const fmtDataOra = new Intl.DateTimeFormat(LOCALE, { timeZone: FUSO, day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function formattaData(iso: string): string {
  return fmtData.format(new Date(iso));
}
export function formattaDataBreve(iso: string): string {
  return fmtDataBreve.format(new Date(iso));
}
export function formattaOra(iso: string): string {
  return fmtOra.format(new Date(iso));
}
export function formattaDataOra(iso: string): string {
  return fmtDataOra.format(new Date(iso));
}
/** "mercoledì 13 gennaio 2027 · 14:30–17:30" */
export function formattaIntervallo(inizioIso: string, fineIso: string): string {
  return `${formattaData(inizioIso)} · ${formattaOra(inizioIso)}–${formattaOra(fineIso)}`;
}
/** "13/01/2027 14:30–17:30" per le tabelle. */
export function formattaIntervalloBreve(inizioIso: string, fineIso: string): string {
  return `${formattaDataBreve(inizioIso)} ${formattaOra(inizioIso)}–${formattaOra(fineIso)}`;
}

export function minutiTra(inizioIso: string, fineIso: string): number {
  return Math.max(0, Math.round((new Date(fineIso).getTime() - new Date(inizioIso).getTime()) / 60_000));
}

/** Giorno (AAAA-MM-GG) di un istante in Europe/Rome: serve per "è oggi". */
export function giornoRoma(d: Date): string {
  const parti = new Intl.DateTimeFormat('en-CA', { timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const v = (t: string) => parti.find((p) => p.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}`;
}
export function eOggi(iso: string, adesso: Date = new Date()): boolean {
  return giornoRoma(new Date(iso)) === giornoRoma(adesso);
}

/** Scostamento di Europe/Rome rispetto a UTC in minuti per un dato istante (60 o 120). */
function scostamentoRoma(d: Date): number {
  const parti = new Intl.DateTimeFormat('en-US', {
    timeZone: FUSO,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(d);
  const v = (t: string) => Number(parti.find((p) => p.type === t)?.value);
  const comeUtc = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second'));
  return Math.round((comeUtc - d.getTime()) / 60_000);
}

/** ISO UTC → valore per <input type="datetime-local"> interpretato in Europe/Rome ("2027-01-13T14:30"). */
export function isoAInputLocale(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const locale = new Date(d.getTime() + scostamentoRoma(d) * 60_000);
  return locale.toISOString().slice(0, 16);
}

/** Valore di <input type="datetime-local"> (orario di Roma) → ISO UTC. */
export function inputLocaleAIso(valore: string): string {
  if (!valore) return '';
  const [data, ora] = valore.split('T');
  const [a, m, g] = (data ?? '').split('-').map(Number);
  const [h, min] = (ora ?? '00:00').split(':').map(Number);
  // prima stima come se fosse UTC, poi correzione con lo scostamento di quell'istante
  const stima = Date.UTC(a!, m! - 1, g!, h ?? 0, min ?? 0);
  const sc1 = scostamentoRoma(new Date(stima));
  let utc = stima - sc1 * 60_000;
  const sc2 = scostamentoRoma(new Date(utc));
  if (sc2 !== sc1) utc = stima - sc2 * 60_000; // a cavallo del cambio d'ora
  return new Date(utc).toISOString();
}
